import { getGoodsReceiptingMatchActionState } from './goodsReceiptingMatchHelpers'
import { shouldOfferAllocateMultiPo } from './invoiceMultiDocRules'
import {
  filterInvoicesInReconciliationAlertWindow,
  filterOrdersInReconciliationAlertWindow,
  isOrderInReconciliationAlertWindow,
} from './reconciliationAlertCutoff'
import { getInvoicePreviewUrl } from './invoiceUploadHelpers'
import config from 'src/config'

export const RECONCILED_STATUSES = [
  'Full accept',
  'Saved',
  'Credit approved by supplier',
  'Awaiting invoice reconciliation',
]

export const CREDIT_WORKFLOW_STATUSES = [
  'Sent to supplier',
  'Rejected by supplier',
  'CN Requested',
]

export const FILTER_KEYS = {
  ALL: 'all',
  /** @deprecated Prefer NEEDS_RECEIPTING — kept for deep-link / storage compat. */
  NEEDS_ACTION: 'needs_action',
  NEEDS_RECEIPTING: 'needs_receipting',
  NEEDS_RECONCILING: 'needs_reconciling',
  NO_INVOICE: 'no_invoice',
  NOT_SAVED: 'not_saved',
  AWAITING_SUPPLIER: 'awaiting_supplier',
  CREDIT_WORKFLOW: 'credit_workflow',
  /** Receipted on GR (delivery closed). */
  COMPLETE: 'complete',
  /** Invoice-reconciled (closed linked invoice / Sent to Xero). */
  RECONCILED: 'reconciled',
  /** Soft-voided POs (Chunk B4). */
  VOIDED: 'voided',
}

export const INVOICE_STATUS = {
  NO_INVOICE: 'No invoice attached',
  ATTACHED: 'Invoice attached',
  RECONCILED: 'Invoice reconciled',
  SENT_TO_XERO: 'Sent to Xero',
}

const NEEDS_ACTION_ORDER_STATUSES = ['Confirmed', 'Changes']

/** Dashboard phone / manual order create (`phoneOrders.js`). */
export const MANUAL_DASHBOARD_PLACED_VIA =
  'Order placed manually on the dashboard'

const LINKED_INVOICE_LIFECYCLES = new Set([
  'attachedToPO',
  'reconciled',
  'sentToXero',
])

/** Invoice lifecycles that count as invoice-reconciled for a GR PO. */
const INVOICE_RECONCILED_LIFECYCLES = new Set(['reconciled', 'sentToXero'])

/**
 * True when delivery is closed on Goods Receipting
 * (Full accept / Saved / Credit approved by supplier).
 *
 * This is receipted — not “invoice reconciled”. Prefer this name over
 * isReconciled when writing new code.
 */
export function isReceipted(order) {
  return RECONCILED_STATUSES.includes(order?.receiptingStatus)
}

/**
 * @deprecated Prefer isReceipted — historically named “reconciled” but means
 * delivery closed on GR, not invoice lifecycle reconciled / sent to Xero.
 */
export function isReconciled(order) {
  return isReceipted(order)
}

export function isInCreditWorkflow(order) {
  return CREDIT_WORKFLOW_STATUSES.includes(order?.receiptingStatus)
}

export function isAwaitingSupplier(order) {
  return order?.status === 'Received'
}

export function getInvoiceStatus(order) {
  const hasInvoice =
    !!order?.invoiceUrl ||
    (Array.isArray(order?.attachedInvoicesUrls) &&
      order.attachedInvoicesUrls.length > 0) ||
    (Array.isArray(order?.attachedInvoiceDetails) &&
      order.attachedInvoiceDetails.length > 0)

  if (order?.sentToXero) {
    return INVOICE_STATUS.SENT_TO_XERO
  }
  if (hasInvoice) {
    return INVOICE_STATUS.ATTACHED
  }
  return INVOICE_STATUS.NO_INVOICE
}

function uniqueTrimmedStrings(values = []) {
  const seen = new Set()
  const result = []
  for (const value of values) {
    const trimmed = String(value || '').trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    result.push(trimmed)
  }
  return result
}

function invoiceLifecycleIsReconciled(value) {
  return INVOICE_RECONCILED_LIFECYCLES.has(String(value || '').trim())
}

function attachedDetailLooksInvoiceReconciled(detail = {}) {
  return (
    invoiceLifecycleIsReconciled(detail.status) ||
    invoiceLifecycleIsReconciled(detail.lifecycle)
  )
}

function idString(value) {
  if (value == null) {
    return ''
  }
  if (typeof value === 'object' && value._id != null) {
    return String(value._id)
  }
  return String(value)
}

/**
 * When PO strings collide across suppliers, only count invoices whose supplier
 * aligns with the order (VenueSupplier id preferred, else Supplier id).
 */
export function invoiceMatchesOrderSupplier(invoice = {}, order = {}) {
  const invoiceVenueSupplierId = idString(invoice.venueSupplierId)
  const orderVenueSupplierId = idString(
    order.venueSupplierId || order.venueSupplier,
  )
  if (invoiceVenueSupplierId && orderVenueSupplierId) {
    return invoiceVenueSupplierId === orderVenueSupplierId
  }

  const invoiceSupplierId = idString(invoice.supplier)
  const orderSupplierId = idString(order.supplier)
  if (invoiceSupplierId && orderSupplierId) {
    return invoiceSupplierId === orderSupplierId
  }

  return true
}

/**
 * Invoices from the Invoices library that are soft-linked / closed against this PO.
 * Used to show invoice # on GR before receipting has stamped invoiceNumber.
 */
export function getLinkedInvoicesForReceiptingOrder(
  order = {},
  uploadedInvoices = [],
) {
  const po = String(order?.purchaseOrderNumber || '').trim()
  if (!po || !Array.isArray(uploadedInvoices) || !uploadedInvoices.length) {
    return []
  }

  return uploadedInvoices.filter(invoice => {
    if (invoice?.isArchived) return false

    let poMatches = false
    if (String(invoice?.purchaseOrderNumber || '').trim() === po) {
      poMatches = true
    }

    const matchedPos = Array.isArray(invoice?.matchedPurchaseOrderNumbers)
      ? invoice.matchedPurchaseOrderNumbers.map(value =>
          String(value || '').trim(),
        )
      : []
    if (!poMatches && matchedPos.includes(po)) {
      poMatches = true
    }

    const allocatedPos = Array.isArray(invoice?.poAllocations)
      ? invoice.poAllocations.map(row =>
          String(row?.purchaseOrderNumber || '').trim(),
        )
      : []
    const explicitlyAllocated = allocatedPos.includes(po)
    if (!poMatches && explicitlyAllocated) {
      poMatches = true
    }

    if (!poMatches) {
      return false
    }

    if (explicitlyAllocated) {
      return true
    }

    const lifecycle = invoice?.status || 'pending'
    if (!LINKED_INVOICE_LIFECYCLES.has(lifecycle)) return false

    return invoiceMatchesOrderSupplier(invoice, order)
  })
}

/**
 * True when this GR PO has at least one linked invoice that is invoice-reconciled
 * (lifecycle reconciled / sentToXero), or the order itself is marked sentToXero.
 *
 * Not the same as receipted, and not the same as “invoice attached”.
 *
 * @param {object} order
 * @param {object[]} [uploadedInvoices] - Invoices library (preferred when details lack status)
 */
/**
 * Express discount lock: an invoice on this purchase order is reconciled or
 * sent to Xero (header link or saved allocation). The order's own sentToXero
 * flag is not enough.
 */
export function isInvoiceReconciledAgainstPurchaseOrder(
  order = {},
  uploadedInvoices = [],
) {
  if (
    Array.isArray(order?.attachedInvoiceDetails) &&
    order.attachedInvoiceDetails.some(attachedDetailLooksInvoiceReconciled)
  ) {
    return true
  }

  const linked = getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices)
  return linked.some(invoice => invoiceLifecycleIsReconciled(invoice?.status))
}

export function isOrderInvoiceReconciled(order = {}, uploadedInvoices = []) {
  if (order?.sentToXero) return true

  if (
    Array.isArray(order?.attachedInvoiceDetails) &&
    order.attachedInvoiceDetails.some(attachedDetailLooksInvoiceReconciled)
  ) {
    return true
  }

  const linked = getLinkedInvoicesForReceiptingOrder(order, uploadedInvoices)
  return linked.some(invoice => invoiceLifecycleIsReconciled(invoice?.status))
}

export function isManualDashboardOrder(order = {}) {
  return order?.placedVia === MANUAL_DASHBOARD_PLACED_VIA
}

/**
 * True once delivery check-in has moved past open Not Saved
 * (Full accept, Saved, partial, credit workflow, etc.).
 */
export function hasGoodsReceiptingProgress(order = {}) {
  const status = String(order?.receiptingStatus || '').trim()
  if (!status || status === 'Not Saved') return false
  return true
}

/**
 * Manual orders are assessed by workflow, not by missing Receipting docs alone.
 * Finance-closed manuals that never started GR do not invent a delivery job.
 * Open / partly checked-in manuals still need Goods Receipting.
 */
export function manualOrderExemptFromGoodsReceipting(
  order = {},
  uploadedInvoices = [],
) {
  if (!isManualDashboardOrder(order)) return false
  if (hasGoodsReceiptingProgress(order)) return false
  return isOrderInvoiceReconciled(order, uploadedInvoices)
}

/**
 * Confirmed / Changes order that still needs delivery closed on GR.
 * Clear of credit workflow and already-receipted rows.
 *
 * @param {object} order
 * @param {object[]} [uploadedInvoices] — used for manual-order finance-closed exemption
 */
export function needsReceipting(order = {}, uploadedInvoices = []) {
  if (order?.isVoided === true || Boolean(order?.voidedAt)) {
    return false
  }
  if (!NEEDS_ACTION_ORDER_STATUSES.includes(order?.status)) {
    return false
  }
  if (isReceipted(order) || isInCreditWorkflow(order)) {
    return false
  }
  if (manualOrderExemptFromGoodsReceipting(order, uploadedInvoices)) {
    return false
  }
  return true
}

/**
 * Confirmed / Changes order that still needs invoice reconcile
 * (no linked invoice in reconciled / sentToXero).
 * Independent of receipting — a row can need both.
 */
export function needsReconciling(order = {}, uploadedInvoices = []) {
  if (order?.isVoided === true || Boolean(order?.voidedAt)) {
    return false
  }
  if (!NEEDS_ACTION_ORDER_STATUSES.includes(order?.status)) {
    return false
  }
  if (isInCreditWorkflow(order)) {
    return false
  }
  if (isOrderInvoiceReconciled(order, uploadedInvoices)) {
    return false
  }
  return true
}

/**
 * Read-only GR Reconciliation status identity for the table cell.
 * Prefers server attachedInvoiceDetails, then receipting invoiceNumber,
 * then Invoices-library links for the same PO.
 *
 * @param {object} order
 * @param {object[]} uploadedInvoices - raw invoices library
 * @param {object[]} [invoiceRows] - mapped rows (same as Match Actions); when
 *   provided, "Invoice reconciled" is suppressed if any open Match candidate
 *   still touches this PO (keeps column aligned with Actions).
 */
export function getGoodsReceiptingInvoiceIdentity(
  order = {},
  uploadedInvoices = [],
  invoiceRows = null,
) {
  const libraryMatches = getLinkedInvoicesForReceiptingOrder(
    order,
    uploadedInvoices,
  )
  const statusFromOrder = getInvoiceStatus(order)
  const status =
    statusFromOrder === INVOICE_STATUS.NO_INVOICE && libraryMatches.length > 0
      ? INVOICE_STATUS.ATTACHED
      : statusFromOrder

  const primaryUrl =
    order?.invoiceUrl ||
    order?.attachedInvoiceDetails?.[0]?.url ||
    order?.attachedInvoicesUrls?.[0] ||
    ''

  const detailNumbers = Array.isArray(order?.attachedInvoiceDetails)
    ? order.attachedInvoiceDetails.map(detail => detail?.invoiceNumber)
    : []
  const libraryNumbers = libraryMatches.map(invoice => invoice?.invoiceNumber)

  const invoiceNumbers = uniqueTrimmedStrings([
    order?.invoiceNumber,
    ...detailNumbers,
    ...libraryNumbers,
  ])

  const urlCount = uniqueTrimmedStrings([
    order?.invoiceUrl,
    ...(Array.isArray(order?.attachedInvoicesUrls)
      ? order.attachedInvoicesUrls
      : []),
    ...(Array.isArray(order?.attachedInvoiceDetails)
      ? order.attachedInvoiceDetails.map(detail => detail?.url)
      : []),
  ]).length

  const attachedCount = Math.max(
    invoiceNumbers.length,
    urlCount,
    Array.isArray(order?.attachedInvoiceDetails)
      ? order.attachedInvoiceDetails.length
      : 0,
    libraryMatches.length,
  )

  // Prefer library lifecycle when every linked invoice is closed — but only
  // when Match Actions would also treat the PO as finished (no open candidates).
  const libraryLifecycles = libraryMatches.map(inv => inv?.status || '')
  const allLibraryClosed =
    libraryMatches.length > 0 &&
    libraryLifecycles.every(
      lifecycle => lifecycle === 'reconciled' || lifecycle === 'sentToXero',
    )
  const anyLibrarySentToXero = libraryLifecycles.some(
    lifecycle => lifecycle === 'sentToXero',
  )

  const rowsForMatch = Array.isArray(invoiceRows)
    ? invoiceRows
    : (uploadedInvoices || []).map(inv => ({
        id: inv.id || inv._id,
        status: inv.status || 'pending',
        isArchived: inv.isArchived,
        purchaseOrderNumber: inv.purchaseOrderNumber,
        matchedPurchaseOrderNumbers: inv.matchedPurchaseOrderNumbers,
        matchSummary: inv.matchSummary,
        linkedOrderId: inv.linkedOrderId,
        matchedOrderIds: inv.matchedOrderIds,
        poAllocations: inv.poAllocations,
        invoiceNumber: inv.invoiceNumber,
      }))
  const receiptedForMatchCheck = {
    ...order,
    receiptingStatus:
      order?.receiptingStatus && order.receiptingStatus !== 'Not Saved'
        ? order.receiptingStatus
        : 'Saved',
  }
  // Same source of truth as the Actions column (Match / Reconciled / Upload).
  const matchActionState = getGoodsReceiptingMatchActionState(
    receiptedForMatchCheck,
    rowsForMatch,
  )

  let resolvedStatus = status
  if (status === INVOICE_STATUS.SENT_TO_XERO || order?.sentToXero) {
    resolvedStatus = INVOICE_STATUS.SENT_TO_XERO
  } else if (matchActionState.kind === 'reconciled') {
    resolvedStatus = anyLibrarySentToXero
      ? INVOICE_STATUS.SENT_TO_XERO
      : INVOICE_STATUS.RECONCILED
  } else if (allLibraryClosed && matchActionState.kind === 'match') {
    // Closed library invoice(s) exist, but another open invoice still
    // touches this PO — Actions show Match; column must not say done.
    resolvedStatus = INVOICE_STATUS.ATTACHED
  } else if (allLibraryClosed) {
    resolvedStatus = anyLibrarySentToXero
      ? INVOICE_STATUS.SENT_TO_XERO
      : INVOICE_STATUS.RECONCILED
  }

  const label = resolvedStatus
  let detail = ''

  if (resolvedStatus === INVOICE_STATUS.SENT_TO_XERO) {
    detail = invoiceNumbers[0] ? `#${invoiceNumbers[0]}` : ''
  } else if (resolvedStatus === INVOICE_STATUS.RECONCILED) {
    if (invoiceNumbers.length === 1) {
      detail = `#${invoiceNumbers[0]}`
    } else if (invoiceNumbers.length > 1) {
      detail = `#${invoiceNumbers[0]} +${invoiceNumbers.length - 1} more`
    }
  } else if (resolvedStatus === INVOICE_STATUS.ATTACHED) {
    if (invoiceNumbers.length === 1) {
      detail = `#${invoiceNumbers[0]}`
    } else if (invoiceNumbers.length > 1) {
      detail = `#${invoiceNumbers[0]} +${invoiceNumbers.length - 1} more`
    } else if (urlCount > 1) {
      // Multi-URL attach without distinct invoice numbers → pages/files, not
      // multiple invoices (do not say “N files” as if they were separate docs).
      detail = 'Multi-page file'
    }
  }

  const displayLabel = detail ? `${label} — ${detail}` : label
  // Library rows expose invoiceTempUrl / key / invoiceUrl — not url/tempUrl.
  const expressBase = config?.apiGateway?.EXPRESS_URL || ''
  const libraryPreviewUrl =
    libraryMatches
      .map(invoice => getInvoicePreviewUrl(invoice, expressBase))
      .map(url => String(url || '').trim())
      .find(Boolean) || ''

  return {
    status: resolvedStatus,
    label,
    detail,
    displayLabel,
    invoiceNumbers,
    attachedCount,
    primaryUrl: primaryUrl || libraryPreviewUrl || '',
    hasFile: Boolean(primaryUrl || libraryPreviewUrl),
    libraryMatches,
  }
}

export function needsAction(order = {}, uploadedInvoices = []) {
  // Legacy outstanding chip — same universe as needsReceipting today.
  return needsReceipting(order, uploadedInvoices)
}

export function isNoInvoiceNeedsAction(order = {}, uploadedInvoices = []) {
  return (
    needsAction(order, uploadedInvoices) &&
    getInvoiceStatus(order) === INVOICE_STATUS.NO_INVOICE
  )
}

export function isNotSavedNeedsAction(order = {}, uploadedInvoices = []) {
  return (
    needsAction(order, uploadedInvoices) &&
    getInvoiceStatus(order) === INVOICE_STATUS.ATTACHED &&
    order?.receiptingStatus === 'Not Saved'
  )
}

/**
 * Order-centric health counts for the Reconciliation Health panel.
 *
 * New fields (Chunk 1):
 * - receipted / percentReceipted — delivery closed on GR
 * - invoiceReconciled — PO has ≥1 linked invoice reconciled / sentToXero
 * - needsReceipting / needsReconciling — clear outstanding jobs
 *
 * Legacy aliases (UI unchanged until Chunk 2):
 * - reconciled === receipted
 * - percentReconciled === percentReceipted
 * - needsAction === needsReceipting
 *
 * @param {object[]} orders
 * @param {object[]} [uploadedInvoices] - optional Invoices library for invoice-reconciled
 */
export function getHealthSummary(orders = [], uploadedInvoices = []) {
  // Alert/health counts ignore pre-cut-off history (see reconciliationAlertCutoff).
  const list = filterOrdersInReconciliationAlertWindow(
    Array.isArray(orders) ? orders : [],
  )
  const invoices = Array.isArray(uploadedInvoices) ? uploadedInvoices : []

  let receipted = 0
  let invoiceReconciled = 0
  let needsReceiptingCount = 0
  let needsReconcilingCount = 0
  let noInvoice = 0
  let notSaved = 0
  let awaitingSupplier = 0
  let creditWorkflow = 0

  for (const order of list) {
    if (order?.isVoided === true || Boolean(order?.voidedAt)) {
      continue
    }
    if (isReceipted(order)) {
      receipted += 1
    }
    if (isOrderInvoiceReconciled(order, invoices)) {
      invoiceReconciled += 1
    }
    if (needsReceipting(order, invoices)) {
      needsReceiptingCount += 1
    }
    if (needsReconciling(order, invoices)) {
      needsReconcilingCount += 1
    }
    if (isNoInvoiceNeedsAction(order, invoices)) {
      noInvoice += 1
    }
    if (isNotSavedNeedsAction(order, invoices)) {
      notSaved += 1
    }
    if (isAwaitingSupplier(order)) {
      awaitingSupplier += 1
    }
    if (isInCreditWorkflow(order)) {
      creditWorkflow += 1
    }
  }

  const total = list.length
  const percentReceipted = total > 0 ? Math.round((receipted / total) * 100) : 0

  return {
    total,
    receipted,
    invoiceReconciled,
    needsReceipting: needsReceiptingCount,
    needsReconciling: needsReconcilingCount,
    noInvoice,
    notSaved,
    awaitingSupplier,
    creditWorkflow,
    percentReceipted,
    // Legacy aliases — panel still reads these until Chunk 2 rename.
    reconciled: receipted,
    needsAction: needsReceiptingCount,
    percentReconciled: percentReceipted,
  }
}

/**
 * View-model for the Health panel headline (Chunk 2).
 * Progress is % receipted; reconciled is the invoice-closed count.
 */
export function getHealthPanelSummaryView(summary = {}) {
  const total = Number(summary.total) || 0
  const receipted = Number(summary.receipted ?? summary.reconciled ?? 0)
  const invoiceReconciled = Number(summary.invoiceReconciled) || 0
  const percentReceipted = Number(
    summary.percentReceipted ?? summary.percentReconciled ?? 0,
  )
  const needsReceiptingCount = Number(
    summary.needsReceipting ?? summary.needsAction ?? 0,
  )
  const needsReconcilingCount = Number(summary.needsReconciling) || 0

  const isFullyReceipted = total > 0 && receipted === total
  const hasOutstandingReconciling = needsReconcilingCount > 0
  const receiptedWithoutInvoiceReconcile = Math.max(
    0,
    receipted - invoiceReconciled,
  )

  const headlineCounts = `${total} order${
    total === 1 ? '' : 's'
  } · ${receipted} receipted · ${invoiceReconciled} reconciled`

  const progressLabel =
    total > 0
      ? `${receipted} of ${total} orders receipted (${percentReceipted}%)`
      : 'No orders in this period'

  const educationLine =
    'Receipted = delivery checked and saved on Goods receipting. Reconciled = a linked invoice is closed (Match & reconcile / Sent to Xero). Attach alone is not reconciled.'

  let successBanner = null
  if (isFullyReceipted) {
    if (hasOutstandingReconciling) {
      successBanner = {
        title: 'All orders in this period are receipted.',
        detail: `${needsReconcilingCount} still need invoice reconcile — linked and closed invoices, not just attached files.`,
        claimsInvoicedAmounts: false,
      }
    } else {
      successBanner = {
        title: 'All orders in this period are receipted and reconciled.',
        detail:
          'Delivery is closed and linked invoices are finished — Spend over time can reflect delivered and invoiced amounts.',
        claimsInvoicedAmounts: true,
      }
    }
  }

  const receiptedNoInvoiceCallout =
    receiptedWithoutInvoiceReconcile > 0
      ? {
          count: receiptedWithoutInvoiceReconcile,
          message: `${receiptedWithoutInvoiceReconcile} receipted order${
            receiptedWithoutInvoiceReconcile === 1 ? '' : 's'
          } still need invoice reconcile (no closed invoice yet).`,
        }
      : null

  return {
    total,
    receipted,
    invoiceReconciled,
    percentReceipted,
    needsReceipting: needsReceiptingCount,
    needsReconciling: needsReconcilingCount,
    receiptedWithoutInvoiceReconcile,
    isFullyReceipted,
    hasOutstandingReconciling,
    headlineCounts,
    progressLabel,
    educationLine,
    successBanner,
    receiptedNoInvoiceCallout,
  }
}

const ALERT_STATUS_FILTER_KEYS = new Set([
  FILTER_KEYS.NEEDS_RECEIPTING,
  FILTER_KEYS.NEEDS_ACTION,
  FILTER_KEYS.NEEDS_RECONCILING,
  FILTER_KEYS.NO_INVOICE,
  FILTER_KEYS.NOT_SAVED,
  FILTER_KEYS.AWAITING_SUPPLIER,
  FILTER_KEYS.CREDIT_WORKFLOW,
])

export function filterOrdersByStatus(
  orders = [],
  filterKey = FILTER_KEYS.ALL,
  uploadedInvoices = [],
) {
  const list = Array.isArray(orders) ? orders : []
  const invoices = Array.isArray(uploadedInvoices) ? uploadedInvoices : []
  const alertScoped = ALERT_STATUS_FILTER_KEYS.has(filterKey)
    ? list.filter(isOrderInReconciliationAlertWindow)
    : list

  switch (filterKey) {
    case FILTER_KEYS.NEEDS_RECEIPTING:
    case FILTER_KEYS.NEEDS_ACTION:
      return alertScoped.filter(order => needsReceipting(order, invoices))
    case FILTER_KEYS.NEEDS_RECONCILING:
      return alertScoped.filter(order => needsReconciling(order, invoices))
    case FILTER_KEYS.NO_INVOICE:
      return alertScoped.filter(order =>
        isNoInvoiceNeedsAction(order, invoices),
      )
    case FILTER_KEYS.NOT_SAVED:
      return alertScoped.filter(order => isNotSavedNeedsAction(order, invoices))
    case FILTER_KEYS.AWAITING_SUPPLIER:
      return alertScoped.filter(isAwaitingSupplier)
    case FILTER_KEYS.CREDIT_WORKFLOW:
      return alertScoped.filter(isInCreditWorkflow)
    case FILTER_KEYS.COMPLETE:
      return list.filter(isReceipted)
    case FILTER_KEYS.RECONCILED:
      return list.filter(order => isOrderInvoiceReconciled(order, invoices))
    case FILTER_KEYS.VOIDED:
      return list.filter(
        order => order?.isVoided === true || Boolean(order?.voidedAt),
      )
    case FILTER_KEYS.ALL:
    default:
      return list
  }
}

export function getRecommendedAction(order = {}, uploadedInvoices = []) {
  if (isAwaitingSupplier(order)) {
    return 'Wait for supplier confirmation, or mark the order as delivered if the delivery has arrived.'
  }

  if (isInCreditWorkflow(order)) {
    return 'Follow up on the credit note request with your supplier.'
  }

  if (needsReceipting(order, uploadedInvoices)) {
    const invoiceStatus = getInvoiceStatus(order)

    if (invoiceStatus === INVOICE_STATUS.NO_INVOICE) {
      return 'Upload the invoice in Invoices & Credit Notes, then receipt this order on Goods receipting (save / Full accept).'
    }

    if (
      invoiceStatus === INVOICE_STATUS.ATTACHED &&
      order?.receiptingStatus === 'Not Saved'
    ) {
      return 'Open this order in Goods receipting, verify lines match the invoice, then receipt it (save / Full accept).'
    }

    if (invoiceStatus === INVOICE_STATUS.ATTACHED) {
      return 'Receipt this order on Goods receipting (save / Full accept) with invoice number and date.'
    }

    return 'Receipt this order on Goods receipting so delivery is closed.'
  }

  if (needsReconciling(order, uploadedInvoices)) {
    const invoiceStatus = getInvoiceStatus(order)
    if (invoiceStatus === INVOICE_STATUS.NO_INVOICE) {
      return 'This order is receipted but has no closed invoice. Attach one and finish Match & reconcile on Invoices & Credit Notes.'
    }
    return 'This order is receipted but not invoice-reconciled. Finish Match & reconcile on Invoices & Credit Notes (attach alone is not enough).'
  }

  return null
}

/** Days before an unmatched invoice is treated as aging. */
export const INVOICE_HEALTH_AGING_DAYS = 7

export const INVOICE_HEALTH_BUCKET = {
  UNMATCHED: 'unmatched',
  POSSIBLE_DUPLICATE: 'possible_duplicate',
  AGING_UNMATCHED: 'aging_unmatched',
  NEEDS_ACTION: 'needs_action',
  READY_TO_RECONCILE: 'ready_to_reconcile',
}

/**
 * Open matching statuses that need customer attention on Invoices
 * (same set as “Needs my review”). Excludes plain Unprocessed.
 */
export const INVOICE_MATCHING_WORK_STATUSES = [
  'match_found',
  'partial_match',
  'needs_action',
  'possible_duplicate',
  'no_match',
  'multiple_matches',
]

const INVOICE_MATCHING_WORK_STATUS_SET = new Set(INVOICE_MATCHING_WORK_STATUSES)

const CLOSED_INVOICE_LIFECYCLE = new Set([
  'reconciled',
  'attachedToPO',
  'sentToXero',
])

function getInvoiceId(invoice) {
  return String(invoice?._id || invoice?.id || '').trim()
}

function getInvoiceMatchingStatus(invoice) {
  return (
    String(invoice?.matchingStatus || 'unprocessed').trim() || 'unprocessed'
  )
}

function invoiceHasOrderLink(invoice) {
  return Boolean(
    invoice?.linkedOrderId ||
      String(invoice?.purchaseOrderNumber || '').trim() ||
      (Array.isArray(invoice?.matchedOrderIds) &&
        invoice.matchedOrderIds.length > 0) ||
      (Array.isArray(invoice?.matchedPurchaseOrderNumbers) &&
        invoice.matchedPurchaseOrderNumbers.length > 0),
  )
}

/** Open (not yet reconciled / archived) invoice. */
export function isOpenInvoice(invoice = {}) {
  if (invoice?.isArchived) return false
  const lifecycle = invoice?.status || 'pending'
  return !CLOSED_INVOICE_LIFECYCLE.has(lifecycle)
}

/**
 * Invoice with no matching OMS order yet — central “attach / find PO” work.
 * Excludes possible duplicates (separate bucket) and invoices that already
 * have a soft link or smart-match recommendation.
 */
export function isUnmatchedInvoice(invoice = {}) {
  if (!isOpenInvoice(invoice)) return false

  const matching = getInvoiceMatchingStatus(invoice)
  if (matching === 'possible_duplicate') return false
  if (
    matching === 'match_found' ||
    matching === 'partial_match' ||
    matching === 'multiple_matches'
  ) {
    return false
  }
  if (matching === 'no_match') return true
  if (matching === 'needs_action' && invoiceHasOrderLink(invoice)) {
    return false
  }

  return !invoiceHasOrderLink(invoice)
}

export function isPossibleDuplicateInvoice(invoice = {}) {
  return (
    isOpenInvoice(invoice) &&
    getInvoiceMatchingStatus(invoice) === 'possible_duplicate'
  )
}

export function isNeedsActionInvoice(invoice = {}) {
  if (!isOpenInvoice(invoice)) return false
  const matching = getInvoiceMatchingStatus(invoice)
  return (
    matching === 'needs_action' ||
    matching === 'multiple_matches' ||
    matching === 'partial_match'
  )
}

export function isReadyToReconcileInvoice(invoice = {}) {
  return (
    isOpenInvoice(invoice) &&
    getInvoiceMatchingStatus(invoice) === 'match_found'
  )
}

/** Open invoice in a Needs-my-review matching status. */
export function isInvoiceMatchingWork(invoice = {}) {
  if (!isOpenInvoice(invoice)) return false
  return INVOICE_MATCHING_WORK_STATUS_SET.has(getInvoiceMatchingStatus(invoice))
}

/** Count for purchasing nav badge / Match queue “Needs my review”. */
export function getInvoiceMatchingWorkCount(
  invoices = [],
  { relatedOrderDatesByInvoiceId = {} } = {},
) {
  const list = filterInvoicesInReconciliationAlertWindow(
    Array.isArray(invoices) ? invoices : [],
    { relatedOrderDatesByInvoiceId },
  )
  return list.reduce(
    (sum, invoice) => sum + (isInvoiceMatchingWork(invoice) ? 1 : 0),
    0,
  )
}

export function getInvoiceAgeDays(invoice = {}, now = new Date()) {
  const raw = invoice?.uploadedDate || invoice?.createdAt
  if (!raw) return null
  const uploaded = new Date(raw)
  if (Number.isNaN(uploaded.getTime())) return null
  const ms = new Date(now).getTime() - uploaded.getTime()
  if (ms < 0) return 0
  return Math.floor(ms / (24 * 60 * 60 * 1000))
}

export function isAgingUnmatchedInvoice(
  invoice = {},
  { now = new Date(), agingDays = INVOICE_HEALTH_AGING_DAYS } = {},
) {
  if (!isUnmatchedInvoice(invoice)) return false
  const ageDays = getInvoiceAgeDays(invoice, now)
  if (ageDays == null) return false
  return ageDays >= Number(agingDays)
}

export function getInvoiceHealthRecommendedAction(invoice = {}) {
  if (!isOpenInvoice(invoice)) return null

  if (isPossibleDuplicateInvoice(invoice)) {
    return 'Review this possible duplicate on Invoices & Credit Notes before attaching or reconciling.'
  }
  if (isReadyToReconcileInvoice(invoice)) {
    return 'Review the recommended match and reconcile with one click on Invoices & Credit Notes.'
  }
  if (shouldOfferAllocateMultiPo(invoice)) {
    return 'Allocate this invoice across the purchase orders it covers on Invoices & Credit Notes.'
  }
  if (getInvoiceMatchingStatus(invoice) === 'multiple_matches') {
    return 'Choose the correct purchase order on Invoices & Credit Notes (Match) — this is not a multi-PO split.'
  }
  if (getInvoiceMatchingStatus(invoice) === 'partial_match') {
    return 'This invoice only partially covers the PO — attach it without closing the order, or upload the remaining invoices.'
  }
  if (isNeedsActionInvoice(invoice)) {
    return 'Resolve the match exception on Invoices & Credit Notes (link a PO, allocate, or force-reconcile if appropriate).'
  }
  if (isUnmatchedInvoice(invoice)) {
    return 'Find and link the correct purchase order, or upload a clearer copy if the invoice could not be read.'
  }
  return null
}

/**
 * Aggregate invoice-centric health buckets for the Reconciliation Health panel
 * and weekly reminder (counts only).
 */
export function getInvoiceHealthSummary(
  invoices = [],
  {
    now = new Date(),
    agingDays = INVOICE_HEALTH_AGING_DAYS,
    relatedOrderDatesByInvoiceId = {},
  } = {},
) {
  const list = filterInvoicesInReconciliationAlertWindow(
    Array.isArray(invoices) ? invoices : [],
    { relatedOrderDatesByInvoiceId },
  )

  let unmatched = 0
  let possibleDuplicates = 0
  let agingUnmatched = 0
  let needsAction = 0
  let readyToReconcile = 0

  for (const invoice of list) {
    if (isUnmatchedInvoice(invoice)) {
      unmatched += 1
      if (isAgingUnmatchedInvoice(invoice, { now, agingDays })) {
        agingUnmatched += 1
      }
    }
    if (isPossibleDuplicateInvoice(invoice)) {
      possibleDuplicates += 1
    }
    if (isNeedsActionInvoice(invoice)) {
      needsAction += 1
    }
    if (isReadyToReconcileInvoice(invoice)) {
      readyToReconcile += 1
    }
  }

  return {
    unmatched,
    possibleDuplicates,
    agingUnmatched,
    needsAction,
    readyToReconcile,
    matchingWorkCount: list.reduce(
      (sum, invoice) => sum + (isInvoiceMatchingWork(invoice) ? 1 : 0),
      0,
    ),
    agingDays: Number(agingDays),
    /** Backward-compatible alias used by the existing orphan callout / email. */
    orphanInvoices: unmatched,
  }
}

/**
 * Compact work-queue rows for the health panel / email, with deep-link metadata.
 * Prefer aging unmatched, then duplicates, then other needs-action.
 */
export function buildInvoiceHealthItems(
  invoices = [],
  {
    now = new Date(),
    agingDays = INVOICE_HEALTH_AGING_DAYS,
    limit = 10,
    relatedOrderDatesByInvoiceId = {},
  } = {},
) {
  const list = filterInvoicesInReconciliationAlertWindow(
    Array.isArray(invoices) ? invoices : [],
    { relatedOrderDatesByInvoiceId },
  )
  const items = []

  for (const invoice of list) {
    if (!isOpenInvoice(invoice)) continue

    let bucket = null
    if (isAgingUnmatchedInvoice(invoice, { now, agingDays })) {
      bucket = INVOICE_HEALTH_BUCKET.AGING_UNMATCHED
    } else if (isPossibleDuplicateInvoice(invoice)) {
      bucket = INVOICE_HEALTH_BUCKET.POSSIBLE_DUPLICATE
    } else if (isReadyToReconcileInvoice(invoice)) {
      bucket = INVOICE_HEALTH_BUCKET.READY_TO_RECONCILE
    } else if (isNeedsActionInvoice(invoice)) {
      bucket = INVOICE_HEALTH_BUCKET.NEEDS_ACTION
    } else if (isUnmatchedInvoice(invoice)) {
      bucket = INVOICE_HEALTH_BUCKET.UNMATCHED
    }

    if (!bucket) continue

    const id = getInvoiceId(invoice)
    items.push({
      id,
      bucket,
      invoiceNumber: invoice.invoiceNumber || '',
      purchaseOrderNumber: invoice.purchaseOrderNumber || '',
      supplierTitle: invoice.supplierTitle || '',
      matchingStatus: getInvoiceMatchingStatus(invoice),
      matchingFailureReason: invoice.matchingFailureReason || null,
      uploadedDate: invoice.uploadedDate || invoice.createdAt || null,
      ageDays: getInvoiceAgeDays(invoice, now),
      recommendedAction: getInvoiceHealthRecommendedAction(invoice),
      deepLink: {
        matching:
          bucket === INVOICE_HEALTH_BUCKET.AGING_UNMATCHED
            ? 'no_match'
            : bucket === INVOICE_HEALTH_BUCKET.UNMATCHED
            ? 'no_match'
            : bucket === INVOICE_HEALTH_BUCKET.POSSIBLE_DUPLICATE
            ? 'possible_duplicate'
            : bucket === INVOICE_HEALTH_BUCKET.READY_TO_RECONCILE
            ? 'match_found'
            : bucket === INVOICE_HEALTH_BUCKET.NEEDS_ACTION
            ? getInvoiceMatchingStatus(invoice)
            : null,
        invoiceId: id || null,
        search: invoice.invoiceNumber || invoice.purchaseOrderNumber || '',
      },
    })
  }

  const bucketPriority = {
    [INVOICE_HEALTH_BUCKET.AGING_UNMATCHED]: 0,
    [INVOICE_HEALTH_BUCKET.POSSIBLE_DUPLICATE]: 1,
    [INVOICE_HEALTH_BUCKET.NEEDS_ACTION]: 2,
    [INVOICE_HEALTH_BUCKET.UNMATCHED]: 3,
    [INVOICE_HEALTH_BUCKET.READY_TO_RECONCILE]: 4,
  }

  items.sort((a, b) => {
    const byBucket =
      (bucketPriority[a.bucket] ?? 99) - (bucketPriority[b.bucket] ?? 99)
    if (byBucket !== 0) return byBucket
    return (b.ageDays ?? -1) - (a.ageDays ?? -1)
  })

  return items.slice(0, Math.max(0, Number(limit) || 0))
}

/** Dashboard path helpers (relative) for health deep-links. */
export function buildReceiptingDeepLinkPath(
  venueId,
  { filter, q, start, end } = {},
) {
  const base = `/v/${venueId}/purchasing/receipting`
  const params = new URLSearchParams()
  if (filter && filter !== FILTER_KEYS.ALL) {
    params.set('filter', filter)
  }
  if (q) {
    params.set('q', q)
  }
  if (start) {
    params.set('start', String(start).trim())
  }
  if (end) {
    params.set('end', String(end).trim())
  }
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

export function buildInvoicesDeepLinkPath(
  venueId,
  {
    matching,
    q,
    invoiceId,
    upload,
    po,
    supplierId,
    supplierTitle,
    /** Apply Match queue “Needs my review” (outstanding) without legacy strip. */
    review,
    /**
     * Open the invoice in the Match workspace (`?invoiceId=`), not the
     * standalone PDF viewer (`/invoice/...`). Ollie actionable CTAs use this.
     */
    openInWorkspace,
  } = {},
) {
  if (invoiceId && !openInWorkspace) {
    return `/invoice/${venueId}/${invoiceId}`
  }
  const base = `/v/${venueId}/purchasing/invoices`
  const params = new URLSearchParams()
  if (review) {
    params.set('review', '1')
  } else if (matching) {
    const value = Array.isArray(matching)
      ? matching.filter(Boolean).join(',')
      : matching
    if (value) params.set('matching', value)
  }
  if (q) params.set('q', q)
  if (upload) params.set('upload', '1')
  if (po) params.set('po', String(po).trim())
  if (supplierId) params.set('supplierId', String(supplierId).trim())
  if (supplierTitle) params.set('supplier', String(supplierTitle).trim())
  if (invoiceId && openInWorkspace) {
    params.set('invoiceId', String(invoiceId).trim())
  }
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

/**
 * GR Match empty state → Invoices upload (same Add Invoices / OCR flow).
 * Prefills PO + supplier when available.
 */
export function buildInvoicesUploadPath(
  venueId,
  { po, supplierId, supplierTitle } = {},
) {
  return buildInvoicesDeepLinkPath(venueId, {
    upload: true,
    po,
    supplierId,
    supplierTitle,
  })
}
